import { DropdownMenuVariant } from "@/components/DropdownMenu";
import { FolderModal } from "@/components/CreateFolderModel";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { useDeleteFolder } from "@/queries";
import { Folder } from "@/types/folders";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { useMemo, useState } from "react";
import {
  ConfirmationModal,
  ConfirmationModalVariant,
} from "@/components/ConfirmationModal";
import { FolderIcon, PencilIcon, TrashIcon } from "@heroicons/react/outline";
import { Card, CardsGrid } from "@/components/ui/Cards/Card";

const FolderGrid = ({ folders }: { folders: Folder[] }) => {
  const [isDeleteFolderModalOpen, setDeleteFolderModalOpen] = useState(false);
  const [isRenameFolderModalOpen, setRenameFolderModalOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const authContext = useAuth();
  const userContext = useUser();

  const folderList = useMemo((): Folder[] => {
    return [...folders].sort((a, b) => a.name.localeCompare(b.name));
  }, [folders]);

  const handleDeleteClick = (folder?: Folder) => {
    setDeleteFolderModalOpen(true);
    setSelectedFolder(folder || null);
  };

  const handleRenameClick = (folder?: Folder) => {
    setRenameFolderModalOpen(true);
    setSelectedFolder(folder || null);
  };
  const deleteFolder = useDeleteFolder(authContext!.userToken!);
  const handleDelete = async () => {
    try {
      const res = await deleteFolder.mutateAsync(selectedFolder?.id!);
      if (res?.success === false) {
        displayErrorToast(res?.message || "Failed to delete folder");
      } else {
        displayToast("Folder deleted", ToastType.success);
      }
      setDeleteFolderModalOpen(false);
    } catch (error: unknown) {
      console.log(error);
    }
  };

  return (
    <>
      <CardsGrid className="mt-4">
        {folderList.map((folder) => (
          <Card<Folder>
            link={`/workspace/${userContext?.activeWorkspaceId}/prompt-folder/${folder.id}`}
            title={folder.name}
            icon={<FolderIcon className="h-6 w-6 text-gray-400" />}
            item={folder}
            actions={[
              {
                id: "rename",
                label: "Rename",
                variant: DropdownMenuVariant.PRIMARY,
                icon: PencilIcon,
                onClick: handleRenameClick,
              },
              {
                id: "delete",
                label: "Delete",
                variant: DropdownMenuVariant.DESTRUCTIVE,
                icon: TrashIcon,
                onClick: handleDeleteClick,
              },
            ]}
          />
        ))}
      </CardsGrid>
      <ConfirmationModal
        title="Are you sure you want to delete this folder?"
        description="Deleting this folder will move its contained prompt templates to home. This action cannot be undone."
        handleOnConfirm={handleDelete}
        isOpen={isDeleteFolderModalOpen}
        setIsOpen={setDeleteFolderModalOpen}
        isLoading={deleteFolder.isLoading}
        leaveButtonText="Cancel"
        saveButtonText="Delete"
        exitButtonVariant="outline"
        saveButtonVariant="destructive"
        headerVariant={ConfirmationModalVariant.destructive}
      />
      {isRenameFolderModalOpen && selectedFolder && (
        <FolderModal
          folder={selectedFolder}
          setOpen={setRenameFolderModalOpen}
        />
      )}
    </>
  );
};

export default FolderGrid;
